import http from "./http";

export interface FlightLeg {
  FlightLegNumber?: string | null;
  DepartureCode?: string | null;
  DepartureName?: string | null;
  DestinationCode?: string | null;
  DestinationName?: string | null;
  StartTime?: string | null;
  DepartureDate?: string | null;
  DepartureTime?: string | null;
  ArrivalDate?: string | null;
  ArrivalTime?: string | null;
  EndTime?: string | null;
  Duration?: string | null;
  IsStop?: boolean | null;
  Layover?: string | null;
  LayoverDuration?: string | null;
  BookingClass?: string | null;
  CabinClass?: string | null;
  CabinClassName?: string | null;
  Seats?: number | null;
  Meal?: string | null;
}

export interface FlightModel {
  Name?: string | null;
  Airline?: string | null;
  AirlineName?: string | null;
  DepartureCode?: string | null;
  DepartureName?: string | null;
  DepartureTime?: string | null;
  ArrivalName?: string | null;
  ArrivalCode?: string | null;
  ArrivalTime?: string | null;
  Stops?: number | null;
  StopTime?: string | null;
  TripDuration?: string | null;
  StopCity?: string | null;
  FlightLegs?: FlightLeg[] | null;
  AirlineLogoUrl?: string | null;
  FreeBaggage?: {
    BagCount?: number | null;
    Weight?: number | null;
    WeightUnit?: string | null;
  } | null;
}

export interface PriceDetail {
  BaseFare?: {
    Amount?: number | null;
    CurrencyCode?: string | null;
  } | null;
  PassengerType?: string | null;
}

export interface TravellerDetail {
  PassengerType?: string | null;
  FirstName?: string | null;
  LastName?: string | null;
  DateOfBirth?: string | null;
  Age?: number | null;
  PhoneNumber?: string | null;
  PassportNumber?: string | null;
  ExpiryDate?: string | null;
  PassportIssuingAuthority?: string | null;
  PassportIssueCountryCode?: string | null;
  Gender?: string | null;
  Title?: string | null;
  Email?: string | null;
  Address?: string | null;
  Country?: string | null;
  CountryCode?: string | null;
  City?: string | null;
  PostalCode?: string | null;
  TicketNumber?: string | null;
  SelectedSeats?: string | null;
  PassengerReferenceNumber?: string | null;
  SelectedBaggages?: string | null;
}

export interface AddManualBookingPayload {
  transaction_id?: string | null;
  status?: string | null;
  PnrReferenceNumber?: string | null;
  PnrDate?: string | null;
  FlightModels?: FlightModel[] | null;
  price?: number | null;
  currency?: string | null;
  adult?: number | null;
  children?: number | null;
  infant?: number | null;
  contactInfo?: {
    email?: string | null;
    phoneNumber?: string | null;
  } | null;
  PriceDetails?: PriceDetail[] | null;
  TravellerDetails?: TravellerDetail[] | null;
  addons_id: string[];
}

export interface Passenger {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  ageGroup?: string;
}

export interface BookingSegment {
  bookingDocumentId?: string;
  segmentIndex?: number;
  direction?: string;
  airline?: string;
  flightNumber?: string;
  from?: string;
  to?: string;
  departureTime?: string;
  arrivalTime?: string;
  stops?: number;
}

export interface Booking {
  id: string;
  booking_id: string;
  airline: string;
  from: string;
  to: string;
  contact: string;
  amount: number;
  status: string;
  date: string;
  pnr?: string;
  email?: string;
  DepartureTime?: string;
  ArrivalTime?: string;
  passengers?: Passenger[];
  segments?: BookingSegment[];
}

export interface GetTransactionsResponse {
  success: boolean;
  message: string;
  data: {
    completeBookings: Booking[];
    pendingBookings: Booking[];
    expiredBookings: Booking[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
    };
  };
}

export const getTransactions = async (
  page: number = 1,
  limit: number = 10,
): Promise<GetTransactionsResponse> => {
  const response = await http.get(
    `/admin/dashboard/flight/manage?page=${page}&limit=${limit}`,
  );
  return response.data;
};

export interface Airline {
  Airline: string;
  AirlineName: string;
  AirlineLogoUrl: string;
}

export interface GetAirlinesResponse {
  success: boolean;
  message: string;
  data: {
    airlines: Airline[];
  };
}

export const getAvailableAirlines = async (): Promise<GetAirlinesResponse> => {
  const response = await http.get("/admin/dashboard/fetch-available-airline");
  return response.data;
};

export interface Airport {
  _id: string;
  AirportCode: string;
  AirportName: string;
  CityCountry: string;
  City: string;
  Country: string;
}

export interface GetAirportsResponse {
  success: boolean;
  message: string;
  data: {
    airports: Airport[];
  };
}

export const getAvailableAirports = async (
  search?: string,
  signal?: AbortSignal,
): Promise<GetAirportsResponse> => {
  const params = search ? { search } : undefined;
  const response = await http.get("/fly/getairports", { params, signal });
  return response.data;
};

export const addManualBooking = async (payload: AddManualBookingPayload) => {
  const response = await http.post(
    "/admin/dashboard/flight/add-manual",
    payload,
  );
  return response.data;
};

export interface CreateTransactionPayload {
  amount: string;
  type: string;
  transactionDate: string;
  status: string;
  paymentReference: string;
}

export const createTransaction = async (payload: CreateTransactionPayload) => {
  const response = await http.post("/admin/transaction/create", payload);
  return response.data;
};

export interface FlightSearchParams {
  pnr?: string;
  origin?: string;
  destination?: string;
  travelDate?: string;
  email?: string;
  phone?: string;
  passengerName?: string;
}

export interface FlightSearchBookingRecord {
  _id: string;
  booking_id: string;
  status: string;
  PnrReferenceNumber?: string | null;
  PnrDate?: string | null;
  FlightModels?: FlightModel | null;
  price?: number | null;
  currency?: string | null;
  adult?: number | null;
  children?: number | null;
  infant?: number | null;
  contactInfo?: {
    email?: string | null;
    phoneNumber?: string | null;
  } | null;
  TravellerDetails?: TravellerDetail[] | null;
  createdAt: string;
}

export interface SearchFlightResponse {
  success: boolean;
  message: string;
  data: {
    flightBookingData: FlightSearchBookingRecord[];
  };
}

export const searchFlight = async (
  params: FlightSearchParams,
): Promise<SearchFlightResponse> => {
  const response = await http.get("/admin/dashboard/flightsearch/", {
    params,
  });
  return response.data;
};

export const mapFlightSearchRecordToBooking = (
  record: FlightSearchBookingRecord,
): Booking => ({
  id: record._id,
  booking_id: record.booking_id,
  airline: record.FlightModels?.AirlineName || "N/A",
  from: record.FlightModels?.DepartureCode || "N/A",
  to: record.FlightModels?.ArrivalCode || "N/A",
  contact: record.contactInfo?.email || "N/A",
  amount: record.price || 0,
  status: record.status,
  date: record.createdAt,
  pnr: record.PnrReferenceNumber || undefined,
  email: record.contactInfo?.email || undefined,
  DepartureTime: record.FlightModels?.DepartureTime || undefined,
  ArrivalTime: record.FlightModels?.ArrivalTime || undefined,
  passengers: record.TravellerDetails?.map((traveller) => ({
    firstName: traveller.FirstName || undefined,
    lastName: traveller.LastName || undefined,
    fullName:
      [traveller.FirstName, traveller.LastName].filter(Boolean).join(" ") ||
      undefined,
    ageGroup: traveller.PassengerType || undefined,
  })),
});

export interface EditFlightTimePayload {
  DepartureTime: string;
  ArrivalTime: string;
}

export interface EditFlightTimeResponse {
  DepartureTime: string;
  ArrivalTime: string;
}

export const editFlightTime = async (
  bookingId: string,
  payload: EditFlightTimePayload,
): Promise<EditFlightTimeResponse> => {
  const response = await http.patch(
    `/admin/dashboard/flight/${bookingId}/edit-time`,
    payload,
  );
  return response.data;
};

export interface RescheduleEmailResponse {
  message: string;
}

export const sendRescheduleEmail = async (
  bookingId: string,
  message?: string,
): Promise<RescheduleEmailResponse> => {
  const response = await http.post(
    `/admin/dashboard/flight/${bookingId}/reschedule-email`,
    { message },
  );
  return response.data;
};
